'use client';

import { c as _c } from "react/compiler-runtime";
import { Dialog } from 'react-aria-components';
import { Avatar } from '@module/mdrt-org/shared/components/avatar/avatar';
import { Chip } from '@module/mdrt-org/shared/components/chip/chip';
import { useGlobalCmsContentContext } from '@module/mdrt-org/shared/components/providers/global-cms-context';
import { Link } from '@module/mdrt-org/shared/i18n/i18n-navigation-link';
import { SEARCH_PERSON_PARAM_KEY } from '@module/mdrt-org/shared/utils/constants/learn-search-constants';
import { isTruthy } from '@module/mdrt-org/shared/utils/helpers/type-compatible-filter-functions';
import { Button } from '@shared/ui-components/button/button';
import { ChipTheme } from '@shared/ui-components/chip/chipTheme';
import { ModalTrigger } from '@shared/ui-components/modal/modal';
import { RenderHTML } from '@shared/ui-components/render-html/render-html';
import { CloseIcon, EmailIcon, PersonBookIcon } from '@shared/ui-components/svgs';
import { GTM_BIO_TYPE_AUTHOR, GTM_BIO_TYPE_FEATURED, GTM_EVENT_NAME_AUTHOR_BIO_CLICK, GTM_EVENT_NAME_AUTHOR_RESOURCELIST_CLICK } from '@shared/utils/constants/gtm';
import { useFormatTranslationString } from '@shared/utils/helpers/format-translation-string';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type PersonWithResourcesCount } from '../../types';
import styles from './person-details.module.scss';
export type PersonDetailsProps = {
  authorLinkAriaLabelText: string;
  authorModalResourcesButtonLabelText: string;
  isFeaturedPerson: boolean;
  memberDetailsButtonAriaLabelText: string;
  memberEmailButtonAriaLabelText: string;
  memberYearsSubheading: null | string | undefined;
  person: PersonWithResourcesCount;
  searchPagePath: string;
};
const getIsInModalString = (isInModal?: boolean) => isInModal ? '-modal' : '';
export const PersonDetails = t0 => {
  const $ = _c(56);
  const {
    authorLinkAriaLabelText,
    authorModalResourcesButtonLabelText,
    isFeaturedPerson,
    memberDetailsButtonAriaLabelText,
    memberEmailButtonAriaLabelText,
    memberYearsSubheading,
    person,
    searchPagePath
  } = t0;
  const formatTranslationString = useFormatTranslationString();
  const imageSource = person.Image?.ImageReference?.Url ?? "";
  const imageAltText = person.Image?.AltText ?? "";
  const name = person.PersonName;
  const memberYears = person.MemberYears && person.MemberYears !== "NM" ? person.MemberYears : null;
  let t1;
  if ($[0] !== memberYears || $[1] !== memberYearsSubheading) {
    t1 = memberYears && (memberYearsSubheading || "MDRT Member: {years} years").replaceAll("{years}", memberYears);
    $[0] = memberYears;
    $[1] = memberYearsSubheading;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const memberYearsText = t1;
  const title = person.Title;
  const city = person.City;
  const country = person.Country;
  let t2;
  if ($[3] !== city || $[4] !== country) {
    t2 = [city, country].filter(isTruthy);
    $[3] = city;
    $[4] = country;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const cityAndCountry = t2.join(", ");
  const emailAddress = person.EmailAddress;
  const showDescriptionButton = Boolean(person.Description);
  let t3;
  if ($[6] !== authorLinkAriaLabelText || $[7] !== formatTranslationString || $[8] !== name) {
    t3 = formatTranslationString(authorLinkAriaLabelText, {
      entity: name
    });
    $[6] = authorLinkAriaLabelText;
    $[7] = formatTranslationString;
    $[8] = name;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const authorLinkAriaLabel = t3;
  const linkToPersonSearchQuery = `${searchPagePath}?q=[${SEARCH_PERSON_PARAM_KEY}=${name}]`;
  const {
    cmsContent
  } = useGlobalCmsContentContext();
  if (!name) {
    return null;
  }
  let t4;
  if ($[10] !== name) {
    t4 = () => sendGtmEvent({
      eventName: GTM_EVENT_NAME_AUTHOR_RESOURCELIST_CLICK,
      label: name
    });
    $[10] = name;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleGtmAuthorResourceListEvent = t4;
  let t5;
  if ($[12] !== imageAltText || $[13] !== imageSource) {
    t5 = isInModal => <Avatar alt={imageAltText} data-testid={`person-details${getIsInModalString(isInModal)}-avatar`} src={imageSource} />;
    $[12] = imageAltText;
    $[13] = imageSource;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const renderAvatarComponent = t5;
  let t6;
  if ($[15] !== authorLinkAriaLabel || $[16] !== cityAndCountry || $[17] !== handleGtmAuthorResourceListEvent || $[18] !== linkToPersonSearchQuery || $[19] !== memberYearsText || $[20] !== name || $[21] !== title) {
    t6 = isInModal_0 => {
      const isInModalString = getIsInModalString(isInModal_0);
      return <div className={styles.info}>{isInModal_0 ? <h4 data-testid={`person-details${isInModalString}-name`}>{name}</h4> : <Link aria-label={authorLinkAriaLabel} className={styles["author-link"]} href={linkToPersonSearchQuery} onClick={handleGtmAuthorResourceListEvent}><h4 data-testid={`person-details${isInModalString}-name`}>{name}</h4></Link>}{memberYearsText && <p className={styles.member} data-testid={`person-details${isInModalString}-member-years`}>{memberYearsText}</p>}{!memberYearsText && title && <p className={styles.title} data-testid={`person-details${isInModalString}-title`}>{title}</p>}{cityAndCountry && <p className={styles.address} data-testid={`person-details${isInModalString}-address`}>{cityAndCountry}</p>}</div>;
    };
    $[15] = authorLinkAriaLabel;
    $[16] = cityAndCountry;
    $[17] = handleGtmAuthorResourceListEvent;
    $[18] = linkToPersonSearchQuery;
    $[19] = memberYearsText;
    $[20] = name;
    $[21] = title;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const renderInfoComponent = t6;
  let t7;
  if ($[23] !== renderAvatarComponent) {
    t7 = renderAvatarComponent(false);
    $[23] = renderAvatarComponent;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== renderInfoComponent) {
    t8 = renderInfoComponent(false);
    $[25] = renderInfoComponent;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== authorLinkAriaLabel || $[28] !== authorModalResourcesButtonLabelText || $[29] !== cmsContent || $[30] !== emailAddress || $[31] !== handleGtmAuthorResourceListEvent || $[32] !== isFeaturedPerson || $[33] !== linkToPersonSearchQuery || $[34] !== memberDetailsButtonAriaLabelText || $[35] !== memberEmailButtonAriaLabelText || $[36] !== name || $[37] !== person.Description || $[38] !== person.resourcesCount || $[39] !== renderAvatarComponent || $[40] !== renderInfoComponent || $[41] !== showDescriptionButton) {
    t9 = showDescriptionButton && <ModalTrigger renderTrigger={t10 => {
      const {
        handleModalOpen
      } = t10;
      return <button aria-label={`${memberDetailsButtonAriaLabelText} ${name}`} className={styles.action} data-testid="person-details-modal-trigger-button" onClick={() => {
        handleModalOpen();
        sendGtmEvent({
          author_bio_type: isFeaturedPerson ? GTM_BIO_TYPE_FEATURED : GTM_BIO_TYPE_AUTHOR,
          eventName: GTM_EVENT_NAME_AUTHOR_BIO_CLICK,
          label: name
        });
      }}><PersonBookIcon /></button>;
    }}>{handleClose => <Dialog><div className={styles.modal}><div className={styles.modal__wrapper}><div className={styles.modal__header}>{renderAvatarComponent(true)}{renderInfoComponent(true)}<Button aria-label={cmsContent.closeButtonLabel} className={styles["modal__close-button"]} dataTestId="person-details-modal-close-button" Icon={CloseIcon} onClick={handleClose} /></div><div className={styles.divider} /><div className={styles.modal__content} data-testid="person-details-modal-description"><RenderHTML as="p" html={person.Description} /></div><div className={styles.divider} /><div className={styles.modal__footer}>{person.resourcesCount > 0 && <Chip aria-label={authorLinkAriaLabel} className={styles.modal__buttons} dataTestId="person-details-modal-resources-button" label={`${authorModalResourcesButtonLabelText} ${person.resourcesCount}`} onClick={handleGtmAuthorResourceListEvent} theme={ChipTheme.Light} to={linkToPersonSearchQuery} />}{emailAddress && <Chip aria-label={`${memberEmailButtonAriaLabelText} ${name}`} className={styles.modal__buttons} dataTestId="person-details-modal-email-button" icon={<EmailIcon />} isExternal={true} label={memberEmailButtonAriaLabelText} theme={ChipTheme.Light} to={`mailto:${emailAddress}`} />}</div></div></div></Dialog>}</ModalTrigger>;
    $[27] = authorLinkAriaLabel;
    $[28] = authorModalResourcesButtonLabelText;
    $[29] = cmsContent;
    $[30] = emailAddress;
    $[31] = handleGtmAuthorResourceListEvent;
    $[32] = isFeaturedPerson;
    $[33] = linkToPersonSearchQuery;
    $[34] = memberDetailsButtonAriaLabelText;
    $[35] = memberEmailButtonAriaLabelText;
    $[36] = name;
    $[37] = person.Description;
    $[38] = person.resourcesCount;
    $[39] = renderAvatarComponent;
    $[40] = renderInfoComponent;
    $[41] = showDescriptionButton;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  let t10;
  if ($[43] !== emailAddress || $[44] !== memberEmailButtonAriaLabelText || $[45] !== name) {
    t10 = emailAddress && <a aria-label={`${memberEmailButtonAriaLabelText} ${name}`} className={styles.action} data-testid="person-details-email" href={`mailto:${emailAddress}`}><EmailIcon /></a>;
    $[43] = emailAddress;
    $[44] = memberEmailButtonAriaLabelText;
    $[45] = name;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== t10 || $[48] !== t9) {
    t11 = <div className={styles.actions}>{t9}{t10}</div>;
    $[47] = t10;
    $[48] = t9;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  let t12;
  if ($[50] !== t11 || $[51] !== t8) {
    t12 = <div className={styles.content}>{t8}{t11}</div>;
    $[50] = t11;
    $[51] = t8;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== t12 || $[54] !== t7) {
    t13 = <div className={styles.wrapper}><div className={styles["content-wrapper"]}>{t7}{t12}</div></div>;
    $[53] = t12;
    $[54] = t7;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  return t13;
};